import { type Address, BITS, type Family, parseAddress } from "./parse";
import asNumberTable from "./tables/as-numbers.json";
import ipv4Table from "./tables/ipv4-space.json";
import ipv6Table from "./tables/ipv6-space.json";
import multicastV4 from "./tables/multicast-v4.json";
import multicastV6 from "./tables/multicast-v6.json";

// IANA's own registries, read the moment the page loads. They are small, they are dedicated under CC0 with the
// intention that software embed them, and they are the only account of who administers a block that needs no request
// to answer — which is why they ride the chunk where the RIRs' delegations and the ROAs are fetched a shard at a time.

// Who IANA gave the block to and on what footing. `blank` where the registry has nothing, which for IPv6 is most of
// the space: the registry names the eighth that has been handed out and says nothing about the rest.
export interface Administration {
  designation: string;
  status: string;
  date: string;
  whois: string;
  // Where the registry answers a query about this block. Shown as an address to copy and never followed: a lookup
  // from this page would be a request to a registry, and the welcome page promises the site makes none.
  rdap: string;
  // The block the answer is about, which is a /8 for IPv4 and whatever width IANA delegated for IPv6.
  cidr: string;
}

export function administrationOf({ family, value }: Address): Administration | undefined {
  return family === "ipv4" ? ipv4Administration(value) : ipv6Administration(value);
}

function ipv4Administration(value: bigint): Administration | undefined {
  // Every /8 is a row and the first octet is which, so this is an index rather than a search.
  const row = IPV4_SPACE.get(Number(value >> 24n));
  if (!row) return undefined;
  const [octet, designation, status, date, whois, rdap] = row;
  return { designation, status, date, whois, rdap, cidr: `${octet}.0.0.0/8` };
}

function ipv6Administration(value: bigint): Administration | undefined {
  // Longest first, so the narrowest registration holding the address is the one that answers.
  for (const { start, end, row } of IPV6_SPACE) {
    if (value < start || value > end) continue;
    return { designation: row[1], status: row[2], date: "", whois: row[3], rdap: row[4], cidr: row[0] };
  }
  return undefined;
}

// The well-known group an address names, which is the whole of what the multicast registries are read for here.
export function multicastGroup({ family, value }: Address): string {
  for (const { start, end, name, mask } of MULTICAST[family]) {
    const at = mask === undefined ? value : value & mask;
    if (at >= start && at <= end) return name;
  }
  return "";
}

// The range of AS numbers a number falls in and who holds it — a registry, or the IETF for the ranges reserved for
// documentation, for private use and for AS112.
export interface AsRange {
  first: number;
  last: number;
  designation: string;
  whois: string;
  rdap: string;
}

export function asRangeOf(number: number): AsRange | undefined {
  for (const [first, last, designation, whois, rdap] of AS_NUMBERS) {
    if (number >= first && number <= last) return { first, last, designation, whois, rdap };
  }
  return undefined;
}

const IPV4_SPACE = new Map(
  (ipv4Table as [number, string, string, string, string, string][]).map((row) => [row[0], row]),
);

const AS_NUMBERS = asNumberTable as [number, number, string, string, string][];

// Parsed once as the chunk loads, so a lookup is arithmetic rather than a re-reading of the registry's spelling.
const IPV6_SPACE = (ipv6Table as [string, string, string, string, string][])
  .map((row) => {
    const { start, end } = range(row[0], "ipv6");
    return { start, end, row, prefix: Number(row[0].split("/")[1]) };
  })
  .sort((left, right) => right.prefix - left.prefix);

// A group written the same way at every scope, which is how IANA publishes more than half of the IPv6 registry:
// `ff0x::fb` is mDNS whether it is asked at link scope or at site scope. Matching it means taking the scope out of
// the address rather than writing the row out sixteen times, which for 127 rows would be the table eight times over.
const SCOPE = ~(0xfn << 112n) & ((1n << 128n) - 1n);

interface Group {
  start: bigint;
  end: bigint;
  name: string;
  mask?: bigint;
}

const MULTICAST: Record<Family, Group[]> = {
  ipv4: groups(multicastV4 as [string, string, string][], "ipv4"),
  ipv6: groups(multicastV6 as [string, string, string][], "ipv6"),
};

// The registry writes both ends of a range out, and the generator drops the end where it repeats the start. A few of
// the IPv6 group IDs are written as a block instead, which is a third spelling of the same pair of ends.
function groups(rows: [string, string, string][], family: Family): Group[] {
  return rows.map(([first, last, name]) => {
    const scoped = first.includes("x");
    const mask = scoped ? SCOPE : undefined;
    const from = scoped ? first.replaceAll("x", "0") : first;
    const to = (scoped ? last.replaceAll("x", "0") : last) || from;
    if (from.includes("/")) return { ...range(from, family), name, mask };
    return { start: value(from, family), end: value(to, family), name, mask };
  });
}

function range(cidr: string, family: Family): { start: bigint; end: bigint } {
  const [body, width] = cidr.split("/");
  const prefix = Number(width);
  const start = value(body, family) & (((1n << BigInt(prefix)) - 1n) << BigInt(BITS[family] - prefix));
  return { start, end: start + (1n << BigInt(BITS[family] - prefix)) - 1n };
}

// A registry this cannot read is a wrong answer waiting to be shown, so it throws as the module loads instead —
// which is what tests/ip-address.test.ts reads every row back to catch.
function value(text: string, family: Family): bigint {
  const address = parseAddress(text, family);
  if (address === null) throw new Error(`Not a ${family} address: ${text}`);
  return address.value;
}
